import moment from 'moment'
import 'moment-timezone'
import i18n from '@/i18n'
import dateMath from './dateMath'

/**
 * The platform's display timezone: the zone asport.lv renders dates in, for every studio today.
 *
 * Set through `VUE_APP_DEFAULT_TIMEZONE` so infrastructure owns the value and it cannot drift from
 * the backend's `AppConstants.DEFAULT_TIMEZONE_ID`, which reads the same value from
 * `ASPORT_DEFAULT_TIMEZONE`. The literal below is the fallback for a build that sets neither.
 *
 * This is **not** "the studio's timezone". They are the same string only because every studio is
 * in Latvia. When that stops being true, the studio's zone becomes data on the organization and is
 * passed into these helpers; this constant stays as the platform-wide default. See [T4] in
 * `docs/backlog.md`.
 */
export const PLATFORM_TIMEZONE = process.env.VUE_APP_DEFAULT_TIMEZONE || 'Europe/Riga'

const timezone = PLATFORM_TIMEZONE;

/** ISO-8601 offset suffix: `Z`, `+03:00` or `+0300`. */
const OFFSET_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/;



// Fail at load, like the backend does with ASPORT_DEFAULT_TIMEZONE. moment accepts an unknown zone

// id, logs to the console and silently uses the browser's zone — which is exactly the bug this

// module exists to prevent, reintroduced by one typo in a build environment and visible to nobody.

if (!dateMath.resolveZone(timezone)) {

  throw new Error(

    `VUE_APP_DEFAULT_TIMEZONE is not a known IANA zone id: "${timezone}". ` +

    'Dates would silently render in the browser timezone.'

  )

}

/** Midnight, as the `HH:mm` string the date/time helpers and pickers use. */
export const DAY_START_TIME = '00:00'

export default {
    /** Date to String formatting */
    formatDateForSystem(date){
      return dateMath.formatDateForSystem(timezone, date);
    },
    /**
     * Parse an instant the API sent (ISO-8601 with `Z`) into a `Date`.
     *
     * Parsing is the one date operation that needs no zone — the offset in the string fixes the
     * point in time. It lives here so "all date handling goes through dateUtil" has no exception a
     * reader has to remember.
     *
     * A string without an offset (`2026-09-25T10:30:00`) is read as the *viewer's* local time, by
     * both `Date` and moment. Nothing can repair that here, so this warns outside production rather
     * than pretending to catch it: our API sends `Instant`, so such a value means the API changed.
     */
    toInstant(value){
      if (value == null) {
        return null;
      }
      if (typeof value === 'string' && !OFFSET_SUFFIX.test(value) && process.env.NODE_ENV !== 'production') {
        console.warn(`dateUtil.toInstant: "${value}" carries no UTC offset, so it is read in the browser timezone.`);
      }
      return moment(value).toDate();
    },

    /**
     * Today in studio time as `YYYY-MM-DD`, for date inputs and for comparing against `end_date`
     * style fields the API sends as plain dates.
     *
     * Never build this from `new Date().toISOString()`: that is the **UTC** date, so between
     * midnight and 03:00 Latvian time it is still yesterday — a membership assigned at 01:00 would
     * start a day early, and "active today" would drop a membership that ends today.
     */
    todayForSystem(){
      return dateMath.todayForSystem(timezone);
    },
    formatDateTimeForSystem(date){
      return moment(date).tz(timezone).format('yyyy-MM-DDTHH:mm');
    },
    /**
     * Time of day in studio time, zero-padded (`09:30`, `18:00`).
     *
     * Zero-padded on purpose: this is the key/label form, so it sorts lexically in chronological
     * order and never has to be parsed back into a date to be displayed. Grouping a schedule by
     * time of day must use this, not a zone-less datetime string - re-parsing such a string reads
     * it as the *viewer's* local time and shifts the whole column for anyone abroad.
     * Use {@link formatTime} for an instant you actually have; this is for the label.
     */
    formatTimeForSystem(date){
      return moment(date).tz(timezone).format('HH:mm');
    },
    formatDate(date){
      return moment(date).tz(timezone).format('yyyy.MM.DD');
    },
    formatDateTime(date){
      return moment(date).tz(timezone).format('yyyy.MM.DD HH:mm');
    },
    /**
     * Dashboard-specific last attended datetime format.
     * - current year: MM.DD HH:mm
     * - other years: YYYY.MM.DD HH:mm
     */
    formatLastAttendedDateTime (date) {
      if (!date) return ''
      const m = moment(date).tz(timezone)
      const now = moment().tz(timezone)
      if (m.year() === now.year()) {
        return m.format('MM.DD HH:mm')
      }
      return m.format('YYYY.MM.DD HH:mm')
    },
    formatShortDate (date) {
      return moment(date).tz(timezone).format('DD.MM');
    },
    /** Day-first long format `DD.MM.YYYY` — the Latvian customer-facing date display
     *  (e.g. "Derīgs līdz 30.06.2026"). Returns '' for null/undefined input. */
    formatDateWithYear (date) {
      if (!date) return ''
      return moment(date).tz(timezone).format('DD.MM.YYYY');
    },
    formatShortDateWithDay(date){
      if (!date) return ''
      const dayName = this.getNameOfDay(date)
      return this.formatShortDate(date) + ` (${dayName})`
    },
    formatShortDateTime (date) {
      return moment(date).tz(timezone).format('DD.MM HH:mm');
    },
    formatTime (date) {
      let time = moment(date).tz(timezone).format('H:mm');
      return time
    },
    /** Zero-padded day of month in studio time (`15`). For date badges that sit next to a time. */
    formatDayOfMonth (date) {
      if (!date) return ''
      return moment(date).tz(timezone).format('DD')
    },
    /**
     * Localised short month name in studio time (`sep.`).
     *
     * Uses `Intl` rather than moment because moment's month names need a per-locale bundle
     * imported into the app, while `Intl` already has them; `timeZone` keeps the studio zone
     * pinned, which a bare `toLocaleDateString` on a Date does not.
     */
    formatShortMonthName (date) {
      if (!date) return ''
      const value = date instanceof Date ? date : new Date(date)
      if (Number.isNaN(value.getTime())) return ''
      return new Intl.DateTimeFormat(i18n.locale, { month: 'short', timeZone: timezone }).format(value)
    },
    formatShortNameDate (date) {
      if (!date) return ''

      const momentDate = moment(date).tz(timezone)
      const now = moment().tz(timezone)
      const today = now.clone().startOf('day')
      const tomorrow = today.clone().add(1, 'day')
      const eventDate = momentDate.clone().startOf('day')

      if (eventDate.isSame(today, 'day')) {
        return i18n.t('common.date.today')
      } else if (eventDate.isSame(tomorrow, 'day')) {
        return i18n.t('common.date.tomorrow')
      } else {
        return momentDate.format('DD.MM')
      }
    },
    formatShortNameDateTime (date) {
      if (!date) return ''

      const momentDate = moment(date).tz(timezone)
      const now = moment().tz(timezone)
      const today = now.clone().startOf('day')
      const tomorrow = today.clone().add(1, 'day')
      const eventDate = momentDate.clone().startOf('day')

      if (eventDate.isSame(today, 'day')) {
        return i18n.t('common.date.today') + ' ' + momentDate.format('HH:mm')
      } else if (eventDate.isSame(tomorrow, 'day')) {
        return i18n.t('common.date.tomorrow') + ' ' + momentDate.format('HH:mm')
      } else {
        return momentDate.format('MM.DD HH:mm')
      }
    },

    /** String to Date formatting */
    toDateFromFormat(date, format){
      return moment.tz(date, format, timezone).toDate();
    },
    toDateFromFullDate(fullDateStr){
      return moment.tz(fullDateStr, 'YYYY-MM-DDTHH:mm', timezone).toDate();
    },
    toDateFromDateTime(dateStr, timeStr){
      return moment.tz(dateStr + 'T' + timeStr, 'YYYY-MM-DDTHH:mm', timezone).toDate()
    },

    /** Date modification */
    getNow(){
      return moment().tz(timezone).toDate();
    },
    addDays(date, daysToAdd){
      return moment(date).tz(timezone).add(daysToAdd, 'days').toDate();
    },
    /**
     * Shift by real minutes. Use this instead of `date.setMinutes(date.getMinutes() + n)`: that
     * does the arithmetic on the viewer's local clock fields, so across a daylight-saving change
     * it moves by an hour more or less than asked.
     */
    addMinutes(date, minutesToAdd){
      return dateMath.addMinutes(timezone, date, minutesToAdd);
    },
    addMonth(date, monthsToAdd){
      return moment(date).tz(timezone).add(monthsToAdd, 'months').toDate();
    },
    set(date, year, month, day){
      return moment(date).tz(timezone)
        .set("date", day)
        .set("months", month)
        .set("year", year)
        .toDate()
    },

    addYear(date, yearsToAdd){
      return moment(date).tz(timezone).add(yearsToAdd, 'years').toDate();
    },

    truncToMonth(date){
      return moment(date).tz(timezone).startOf('month').toDate();
    },

    /** 31 December of the studio-time year containing `date`, at 00:00. */

    endOfYear(date){

      return moment(date).tz(timezone).endOf('year').startOf('day').toDate();

    },



    /** 1 January of the studio-time year containing `date`. */
    truncToYear(date){
      return moment(date).tz(timezone).startOf('year').toDate();
    },

    /**
     * Day of week in studio time, ISO numbering: 1 = Monday … 7 = Sunday.
     *
     * `Date.getDay()` answers for the viewer's timezone and numbers Sunday as 0, so a trainer
     * abroad picking "every Monday" can generate Sundays.
     */
    isoWeekday(date){
      return dateMath.isoWeekday(timezone, date);
    },

    truncToDay(date){
      return moment(date).tz(timezone).startOf('day').toDate();
    },

    /**
     * Monday 00:00 of the studio-time week containing `date`.
     *
     * Week bucketing must not be built from `Date.getDay()` / `setHours(0,0,0,0)`: those cut the
     * week at the *viewer's* midnight, so a participant abroad gets different buckets than the
     * studio does for the same bookings.
     */
    startOfWeekMonday (date) {
      return moment(date).tz(timezone).startOf('isoWeek').toDate()
    },

    /**
     * `YYYY-MM-DD` identity of the studio-time calendar day.
     *
     * Used as a map key to line up locally generated days with day buckets from the API. Both
     * sides must go through this function or they will not match.
     */
    toDayKey (date) {
      if (!date) return ''
      const value = moment(date).tz(timezone)
      return value.isValid() ? value.format('YYYY-MM-DD') : ''
    },

    /** Last day of the same calendar month at 00:00 LV time. */
    endOfMonth(date){
      return moment(date).tz(timezone).endOf('month').startOf('day').toDate();
    },

    /** True if {@code a} is strictly after {@code b} at the given granularity (default 'day'). */
    isAfter(a, b, granularity){
      return moment(a).tz(timezone).isAfter(moment(b).tz(timezone), granularity || 'day');
    },

    /** Timezone utilities */

    /**
     * Minutes the viewer's device clock differs from studio time right now.
     *
     * Positive = the device is ahead of the studio (east of it). `0` means the device shows the
     * same wall clock as the studio, which is what almost every viewer sees. Compared as an
     * **offset**, never by zone name: Europe/Helsinki is a different name from Europe/Riga but the
     * same clock, and someone in Helsinki must not be told they are somewhere else.
     *
     * Read at call time rather than cached, because the answer changes when either side crosses a
     * daylight-saving boundary.
     */
    viewerOffsetMinutesFromStudio () {
      return moment().utcOffset() - moment().tz(timezone).utcOffset()
    },

    /** True when the viewer's device shows a different wall clock than the studio. */
    isViewerInDifferentTimezone () {
      return this.viewerOffsetMinutesFromStudio() !== 0
    },

    /**
     * The viewer's offset from studio time, for display: `+3 h`, `-10 h`, `+5:30 h`.
     * Returns '' when there is no difference. Half-hour zones (e.g. India) keep their minutes.
     */
    formatViewerOffsetFromStudio () {
      const totalMinutes = this.viewerOffsetMinutesFromStudio()
      if (totalMinutes === 0) return ''
      const sign = totalMinutes > 0 ? '+' : '-'
      const absMinutes = Math.abs(totalMinutes)
      const hours = Math.floor(absMinutes / 60)
      const minutes = absMinutes % 60
      const value = minutes === 0 ? `${hours}` : `${hours}:${String(minutes).padStart(2, '0')}`
      return `${sign}${value} h`
    },

    /** Date utilities */
    getNameOfDay(date){
      //Date should be of type date
      if (!date){
        return '';
      }
      let day = moment(date).tz(timezone).day();
      let translate = 'common.date.day-' + day
      let name = i18n.t(translate)
      if (name === translate){
        return ''
      }
      return name
    }
}